import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Table, Modal, Form, Input, Select, DatePicker, Upload, message, Tag, Badge, Popconfirm, Divider, InputNumber, Checkbox, Radio } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, TagOutlined, FolderOutlined, CalendarOutlined, CameraOutlined, UserOutlined, MoreOutlined, FilterOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import ImageViewer from '../components/ImageViewer';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Dragger } = Upload;

const Expenses = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [expenseModalVisible, setExpenseModalVisible] = useState(false);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [tagModalVisible, setTagModalVisible] = useState(false);
  const [currentExpense, setCurrentExpense] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentTag, setCurrentTag] = useState(null);
  const [expenseForm] = Form.useForm();
  const [categoryForm] = Form.useForm();
  const [tagForm] = Form.useForm();
  const [previewImage, setPreviewImage] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [qrcodeVisible, setQrcodeVisible] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);

  const [financialSummary, setFinancialSummary] = useState({
    totalIncome: 0,
    totalExpense: 0,
    balance: 0
  });

  useEffect(() => {
    fetchExpenses();
    fetchCategories();
    fetchTags();
    fetchFinancialSummary();
  }, [searchTerm, categoryFilter, dateRange]);

  // 从记账数据中计算财务汇总
  const calculateFinancialSummary = (expenses) => {
    const totalIncome = expenses.reduce((sum, item) => {
      if (item.type === 'income') {
        return sum + (typeof item.amount === 'number' ? item.amount : parseFloat(item.amount) || 0);
      }
      return sum;
    }, 0);
    
    const totalExpense = expenses.reduce((sum, item) => {
      if (item.type === 'expense') {
        return sum + (typeof item.amount === 'number' ? item.amount : parseFloat(item.amount) || 0);
      }
      return sum;
    }, 0);
    
    const balance = totalIncome - totalExpense;
    
    setFinancialSummary({
      totalIncome,
      totalExpense,
      balance
    });
  };

  // 获取财务汇总数据（当月所有记录）
  const fetchFinancialSummary = async () => {
    try {
      const params = {
        limit: 1000, // 设置一个较大的值，确保获取所有记录
        search: searchTerm,
        category_id: categoryFilter,
      };

      if (dateRange) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      // PC版默认不设置日期范围，显示所有记录

      const response = await axiosInstance.get('/expenses', { params });
      const responseData = response.data?.data || {};
      const expensesList = responseData.expenses || [];
      
      // 计算财务汇总
      calculateFinancialSummary(expensesList);
    } catch (error) {
      console.error('获取财务汇总失败:', error);
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
        category_id: categoryFilter,
      };

      if (dateRange) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      // PC版默认不设置日期范围，显示所有记录

      const response = await axiosInstance.get('/expenses', { params });
      const responseData = response.data?.data || {};
      const expensesList = responseData.expenses || [];
      setExpenses(expensesList);
      setTotal(responseData.pagination?.total || 0);
    } catch (error) {
      message.error('获取账目失败');
      console.error('获取账目失败:', error);
      setExpenses([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/expenses/categories');
      setCategories(response.data.data || []);
    } catch (error) {
      message.error('获取分类失败');
      console.error('获取分类失败:', error);
      setCategories([]);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axiosInstance.get('/expenses/tags');
      setTags(response.data.data || []);
    } catch (error) {
      message.error('获取标签失败');
      console.error('获取标签失败:', error);
      setTags([]);
    }
  };

  const handleExpenseCreate = () => {
    setCurrentExpense(null);
    const today = dayjs();
    setSelectedDate(today);
    expenseForm.resetFields();
    expenseForm.setFieldsValue({ date: today });
    setExpenseModalVisible(true);
  };

  const handleExpenseEdit = (expense) => {
    setCurrentExpense(expense);
    const expenseDate = dayjs(expense.date);
    setSelectedDate(expenseDate);
    expenseForm.setFieldsValue({
      type: expense.type || 'expense',
      amount: expense.amount,
      description: expense.description,
      category_id: expense.category_id,
      date: expenseDate,
      tags: expense.tags?.map(tag => tag.id) || [],
    });
    setExpenseModalVisible(true);
  };

  const handleExpenseDelete = async (id) => {
    try {
      await axiosInstance.delete(`/expenses/${id}`);
      message.success('删除成功');
      fetchExpenses();
      fetchFinancialSummary();
    } catch (error) {
      message.error('删除失败');
      console.error('删除账目失败:', error);
    }
  };

  const handleExpenseSubmit = async (values) => {
    try {
      // 如果没有type值，根据分类获取
      let type = values.type;
      if (!type && values.category_id) {
        const category = categories.find(cat => cat.id === values.category_id);
        type = category?.type || 'expense';
      }
      
      const expenseData = {
        amount: values.amount,
        description: values.description,
        category_id: values.category_id,
        date: values.date.format('YYYY-MM-DD'),
        type: type || 'expense',
        tags: values.tags
      };

      if (currentExpense) {
        await axiosInstance.put(`/expenses/${currentExpense.id}`, expenseData);
        message.success('更新成功');
      } else {
        await axiosInstance.post('/expenses', expenseData);
        message.success('创建成功');
      }

      setExpenseModalVisible(false);
      fetchExpenses();
      fetchFinancialSummary();
    } catch (error) {
      message.error('操作失败');
      console.error('保存账目失败:', error);
    }
  };

  const handleCategoryCreate = () => {
    setCurrentCategory(null);
    categoryForm.resetFields();
    setCategoryModalVisible(true);
  };

  const handleCategoryEdit = (category) => {
    setCurrentCategory(category);
    categoryForm.setFieldsValue({
      name: category.name,
      type: category.type || 'expense',
      color: category.color,
      icon: category.icon || 'folder',
    });
  };

  const handleCategoryDelete = async (id) => {
    try {
      await axiosInstance.delete(`/expenses/categories/${id}`);
      message.success('删除分类成功');
      fetchCategories();
    } catch (error) {
      message.error('删除分类失败');
      console.error('删除分类失败:', error);
    }
  };

  const handleCategorySubmit = async (values) => {
    try {
      if (currentCategory) {
        await axiosInstance.put(`/expenses/categories/${currentCategory.id}`, values);
        message.success('更新分类成功');
      } else {
        await axiosInstance.post('/expenses/categories', values);
        message.success('创建分类成功');
      }
      setCurrentCategory(null);
      categoryForm.resetFields();
      fetchCategories();
    } catch (error) {
      message.error('操作失败');
      console.error('保存分类失败:', error);
    }
  };

  const handleTagCreate = () => {
    setCurrentTag(null);
    tagForm.resetFields();
    setTagModalVisible(true);
  };

  const handleTagEdit = (tag) => {
    setCurrentTag(tag);
    tagForm.setFieldsValue({
      name: tag.name,
      color: tag.color,
    });
  };

  const handleTagDelete = async (id) => {
    try {
      await axiosInstance.delete(`/expenses/tags/${id}`);
      message.success('删除标签成功');
      fetchTags();
    } catch (error) {
      message.error('删除标签失败');
      console.error('删除标签失败:', error);
    }
  };

  // 生成手机记账二维码
  const generateQRCode = async () => {
    try {
      // 生成设备码用于自动登录
      const deviceCodeResponse = await axiosInstance.post('/auth/device-code');
      const code = deviceCodeResponse.data?.code || '';
      
      // 构建手机记账页面URL
      const baseUrl = window.location.origin;
      const params = new URLSearchParams();
      if (code) params.append('code', code);
      
      const mobileUrl = `${baseUrl}/mobile/expenses?${params.toString()}`;
      
      // 使用API生成二维码
      // 注意：这里使用了一个公共的二维码生成API，实际项目中应该使用后端生成
      const encodedUrl = encodeURIComponent(mobileUrl);
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodedUrl}`;
      
      setQrCodeData({
        qrCodeUrl: qrCodeUrl,
        mobileUrl: mobileUrl
      });
      setQrcodeVisible(true);
    } catch (error) {
      console.error('生成二维码失败:', error);
      message.error('二维码生成失败');
    }
  };

  const handleTagSubmit = async (values) => {
    try {
      if (currentTag) {
        await axiosInstance.put(`/expenses/tags/${currentTag.id}`, values);
        message.success('更新标签成功');
      } else {
        await axiosInstance.post('/expenses/tags', values);
        message.success('创建标签成功');
      }
      setCurrentTag(null);
      tagForm.resetFields();
      fetchTags();
    } catch (error) {
      message.error('操作失败');
      console.error('保存标签失败:', error);
    }
  };



  const handleResetFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setDateRange(null);
    setCurrentPage(1);
  };

  const getCategoryById = (id) => {
    if (!Array.isArray(categories)) {
      return null;
    }
    return categories.find(cat => cat.id === id);
  };

  const getTagById = (id) => {
    if (!Array.isArray(tags)) {
      return null;
    }
    return tags.find(tag => tag.id === id);
  };

  const columns = [
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'income' ? 'green' : 'red'}>
          {type === 'income' ? '收入' : '支出'}
        </Tag>
      ),
      filters: [
        { text: '收入', value: 'income' },
        { text: '支出', value: 'expense' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record) => (
        <span style={{ fontSize: '16px', fontWeight: 'bold', color: record.type === 'income' ? '#52c41a' : '#ff4d4f' }}>
          ¥{(typeof amount === 'number' ? amount : parseFloat(amount) || 0).toFixed(2)}
        </span>
      ),
      sorter: (a, b) => (typeof a.amount === 'number' ? a.amount : parseFloat(a.amount) || 0) - (typeof b.amount === 'number' ? b.amount : parseFloat(b.amount) || 0),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '分类',
      dataIndex: 'category_id',
      key: 'category',
      render: (categoryId) => {
        const category = getCategoryById(categoryId);
        return category ? (
          <Badge color={category.color} text={category.name} />
        ) : '-';
      },
      filters: Array.isArray(categories) ? categories.map(cat => ({
        text: cat.name,
        value: cat.id,
      })) : [],
      onFilter: (value, record) => record.category_id === value,
    },
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
      sorter: (a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf(),
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags) => (
        <Space>
          {Array.isArray(tags) && tags.length > 0 ? (
            tags.map(tag => (
              <Tag key={tag.id} color={tag.color}>{tag.name}</Tag>
            ))
          ) : '-'}
        </Space>
      ),
    },
    {
      title: '添加人',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      render: (createdByName) => createdByName || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt) => createdAt ? dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss') : '-',
      sorter: (a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf(),
    },

    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            onClick={() => handleExpenseEdit(record)}
            size="small"
          />
          <Popconfirm
            title="确定要删除这条账目吗？"
            onConfirm={() => handleExpenseDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const colorOptions = [
    { label: '红色', value: '#ff4d4f' },
    { label: '橙色', value: '#fa8c16' },
    { label: '黄色', value: '#faad14' },
    { label: '绿色', value: '#52c41a' },
    { label: '青色', value: '#13c2c2' },
    { label: '蓝色', value: '#1890ff' },
    { label: '紫色', value: '#722ed1' },
    { label: '粉色', value: '#eb2f96' },
  ];

  return (
    <div className="expenses-container" style={{ padding: '10px 16px' }}>
      <div className="expenses-header" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: '24px', margin: 0 }}>记账管理</h1>
        <Space>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleExpenseCreate}
          >
            记一笔
          </Button>
          <Button 
            icon={<FolderOutlined />} 
            onClick={handleCategoryCreate}
          >
            分类管理
          </Button>
          <Button 
            icon={<TagOutlined />} 
            onClick={handleTagCreate}
          >
            标签管理
          </Button>
          <Button 
            icon={<CameraOutlined />} 
            onClick={() => window.open('/mobile/expenses', '_blank')}
          >
            手机记账
          </Button>
        </Space>
      </div>

      {/* 财物汇总卡片 */}
      <Card style={{ marginBottom: '20px' }}>
        <div className="financial-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div className="financial-summary-item" style={{ padding: '15px', background: '#f0f9ff', borderRadius: '8px', textAlign: 'center' }}>
            <p style={{ color: '#1890ff', margin: 0 }}>总收入</p>
            <p style={{ fontSize: 'clamp(18px, 3vw, 24px)', fontWeight: 'bold', margin: '10px 0 0 0' }}>¥{(typeof financialSummary.totalIncome === 'number' ? financialSummary.totalIncome : parseFloat(financialSummary.totalIncome) || 0).toFixed(2)}</p>
          </div>
          <div className="financial-summary-item" style={{ padding: '15px', background: '#fff2f0', borderRadius: '8px', textAlign: 'center' }}>
            <p style={{ color: '#ff4d4f', margin: 0 }}>总支出</p>
            <p style={{ fontSize: 'clamp(18px, 3vw, 24px)', fontWeight: 'bold', margin: '10px 0 0 0' }}>¥{(typeof financialSummary.totalExpense === 'number' ? financialSummary.totalExpense : parseFloat(financialSummary.totalExpense) || 0).toFixed(2)}</p>
          </div>
          <div className="financial-summary-item" style={{ padding: '15px', background: '#f6ffed', borderRadius: '8px', textAlign: 'center' }}>
            <p style={{ color: '#52c41a', margin: 0 }}>结余</p>
            <p style={{ fontSize: 'clamp(18px, 3vw, 24px)', fontWeight: 'bold', margin: '10px 0 0 0', color: (typeof financialSummary.balance === 'number' ? financialSummary.balance : parseFloat(financialSummary.balance) || 0) >= 0 ? '#52c41a' : '#ff4d4f' }}>¥{(typeof financialSummary.balance === 'number' ? financialSummary.balance : parseFloat(financialSummary.balance) || 0).toFixed(2)}</p>
          </div>
        </div>
      </Card>

      <Card style={{ marginBottom: '20px' }}>
        <div className="expenses-filters" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', flex: 1 }}>
            <Input
              placeholder="搜索描述或备注"
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 200 }}
            />
            <Select
              placeholder="选择分类"
              value={categoryFilter}
              onChange={setCategoryFilter}
              style={{ width: 150 }}
              allowClear
            >
              {Array.isArray(categories) ? categories.map(category => (
                <Option key={category.id} value={category.id}>
                  <Badge color={category.color} text={category.name} />
                </Option>
              )) : []}
            </Select>
            <RangePicker
              placeholder={['开始日期', '结束日期']}
              value={dateRange}
              onChange={setDateRange}
              style={{ width: 300 }}
            />
            <Button
              icon={<FilterOutlined />}
              onClick={() => setCurrentPage(1)}
            >
              筛选
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleResetFilters}
            >
              重置
            </Button>
          </div>
          <Button
            icon={<SyncOutlined />}
            onClick={() => window.location.reload()}
          />
        </div>

        <Table
          columns={columns}
          dataSource={expenses}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
        />
      </Card>

      {/* 记账模态框 */}
      <Modal
        className="expense-modal"
        title={currentExpense ? '编辑账目' : '记一笔'}
        open={expenseModalVisible}
        onCancel={() => setExpenseModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          className="expense-form"
          form={expenseForm}
          onFinish={handleExpenseSubmit}
          layout="vertical"
          onValuesChange={(changedValues, allValues) => {
            if (changedValues.category_id) {
              const category = categories.find(cat => cat.id === changedValues.category_id);
              if (category) {
                expenseForm.setFieldsValue({ type: category.type });
              }
            }
          }}
        >


          <Form.Item
            label="金额"
            name="amount"
            rules={[{ required: true, message: '请输入金额' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="请输入金额"
              min={0.01}
              step={0.01}
              formatter={(value) => `¥ ${value}`}
              parser={(value) => value.replace(/^¥\s*/, '')}
            />
          </Form.Item>

          <Form.Item
            label="用途"
            name="description"
            rules={[{ required: true, message: '请输入用途' }]}
          >
            <Input placeholder="请输入用途" />
          </Form.Item>

          <Form.Item
            label="分类"
            name="category_id"
            rules={[{ required: true, message: '请选择分类' }]}
          >
            <Select placeholder="请选择分类">
              {Array.isArray(categories) ? categories.map(category => (
                <Option key={category.id} value={category.id}>
                  <Badge color={category.color} text={category.name} />
                </Option>
              )) : []}
            </Select>
          </Form.Item>

          <Form.Item
            label="日期"
            name="date"
            rules={[{ required: true, message: '请选择日期' }]}
          >
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <DatePicker 
                style={{ width: '60%' }} 
                value={selectedDate} 
                onChange={(date) => setSelectedDate(date)}
              />
              <div style={{ width: '40%', display: 'flex', gap: '5px' }}>
                <Button size="small" style={{ flex: 1 }} onClick={() => {
                  const yesterday = dayjs().subtract(1, 'day');
                  setSelectedDate(yesterday);
                  expenseForm.setFieldsValue({ date: yesterday });
                }}>昨天</Button>
                <Button size="small" style={{ flex: 1 }} onClick={() => {
                  const twoDaysAgo = dayjs().subtract(2, 'day');
                  setSelectedDate(twoDaysAgo);
                  expenseForm.setFieldsValue({ date: twoDaysAgo });
                }}>前天</Button>
                <Button size="small" style={{ flex: 1 }} onClick={() => {
                  const threeDaysAgo = dayjs().subtract(3, 'day');
                  setSelectedDate(threeDaysAgo);
                  expenseForm.setFieldsValue({ date: threeDaysAgo });
                }}>三天前</Button>
              </div>
            </div>
          </Form.Item>

          <Form.Item
            label="标签"
            name="tags"
          >
            <Select
              mode="multiple"
              placeholder="请选择标签"
              style={{ width: '100%' }}
            >
              {Array.isArray(tags) ? tags.map(tag => (
                <Option key={tag.id} value={tag.id}>
                  <Tag color={tag.color}>{tag.name}</Tag>
                </Option>
              )) : []}
            </Select>
          </Form.Item>







          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setExpenseModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 分类管理模态框 */}
      <Modal
        title="分类管理"
        open={categoryModalVisible}
        onCancel={() => setCategoryModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '15px' }}>创建/编辑分类</h3>
          <Form
            form={categoryForm}
            onFinish={handleCategorySubmit}
            layout="vertical"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <Form.Item
                  label="分类名称"
                  name="name"
                  rules={[{ required: true, message: '请输入分类名称' }]}
                >
                  <Input placeholder="请输入分类名称" />
                </Form.Item>

                <Form.Item
                  label="分类类型"
                  name="type"
                  rules={[{ required: true, message: '请选择分类类型' }]}
                >
                  <Radio.Group style={{ width: '100%' }}>
                    <Radio.Button value="expense" style={{ flex: 1 }}>支出</Radio.Button>
                    <Radio.Button value="income" style={{ flex: 1 }}>收入</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </div>

              <Form.Item
                label="分类颜色"
                name="color"
                rules={[{ required: true, message: '请选择分类颜色' }]}
              >
                <Select placeholder="请选择分类颜色">
                  {colorOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ 
                          width: 16, 
                          height: 16, 
                          borderRadius: 8, 
                          backgroundColor: option.value, 
                          marginRight: 8 
                        }} />
                        {option.label}
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <Form.Item>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => {
                  setCurrentCategory(null);
                  categoryForm.resetFields();
                }}>
                  重置
                </Button>
                <Button type="primary" htmlType="submit">
                  {currentCategory ? '更新' : '创建'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>

        <Divider />

        <div>
          <h3 style={{ fontSize: '16px', marginBottom: '15px' }}>已添加的分类</h3>
          {Array.isArray(categories) && categories.length > 0 ? (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {categories.map(category => (
                <div key={category.id} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  padding: '10px', 
                  border: '1px solid #e8e8e8', 
                  borderRadius: '4px', 
                  marginBottom: '10px' 
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ 
                      width: 20, 
                      height: 20, 
                      borderRadius: 10, 
                      backgroundColor: category.color, 
                      marginRight: 10 
                    }} />
                    <span>{category.name}</span>
                    <Tag size="small" color={category.type === 'income' ? 'green' : 'red'}>
                      {category.type === 'income' ? '收入' : '支出'}
                    </Tag>
                  </div>
                  <Space>
                    <Button 
                      type="link" 
                      icon={<EditOutlined />} 
                      onClick={() => handleCategoryEdit(category)}
                    >
                      编辑
                    </Button>
                    <Popconfirm
                      title="确定要删除这个分类吗？"
                      onConfirm={() => handleCategoryDelete(category.id)}
                      okText="确定"
                      cancelText="取消"
                    >
                      <Button 
                        type="link" 
                        danger 
                        icon={<DeleteOutlined />}
                      >
                        删除
                      </Button>
                    </Popconfirm>
                  </Space>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
              暂无分类，点击上方按钮创建
            </div>
          )}
        </div>
      </Modal>

      {/* 标签管理模态框 */}
      <Modal
        title="标签管理"
        open={tagModalVisible}
        onCancel={() => setTagModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '15px' }}>创建/编辑标签</h3>
          <Form
            form={tagForm}
            onFinish={handleTagSubmit}
            layout="vertical"
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <Form.Item
                label="标签名称"
                name="name"
                rules={[{ required: true, message: '请输入标签名称' }]}
              >
                <Input placeholder="请输入标签名称" />
              </Form.Item>

              <Form.Item
                label="标签颜色"
                name="color"
                rules={[{ required: true, message: '请选择标签颜色' }]}
              >
                <Select placeholder="请选择标签颜色">
                  {colorOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ 
                          width: 16, 
                          height: 16, 
                          borderRadius: 8, 
                          backgroundColor: option.value, 
                          marginRight: 8 
                        }} />
                        {option.label}
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <Form.Item>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => {
                  setCurrentTag(null);
                  tagForm.resetFields();
                }}>
                  重置
                </Button>
                <Button type="primary" htmlType="submit">
                  {currentTag ? '更新' : '创建'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>

        <Divider />

        <div>
          <h3 style={{ fontSize: '16px', marginBottom: '15px' }}>已添加的标签</h3>
          {Array.isArray(tags) && tags.length > 0 ? (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {tags.map(tag => (
                <div key={tag.id} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  padding: '10px', 
                  border: '1px solid #e8e8e8', 
                  borderRadius: '4px', 
                  marginBottom: '10px' 
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ 
                      width: 20, 
                      height: 20, 
                      borderRadius: 10, 
                      backgroundColor: tag.color, 
                      marginRight: 10 
                    }} /> 
                    <span>{tag.name}</span>
                  </div>
                  <Space>
                    <Button 
                      type="link" 
                      icon={<EditOutlined />} 
                      onClick={() => handleTagEdit(tag)}
                    >
                      编辑
                    </Button>
                    <Popconfirm
                      title="确定要删除这个标签吗？"
                      onConfirm={() => handleTagDelete(tag.id)}
                      okText="确定"
                      cancelText="取消"
                    >
                      <Button 
                        type="link" 
                        danger 
                        icon={<DeleteOutlined />}
                      >
                        删除
                      </Button>
                    </Popconfirm>
                  </Space>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
              暂无标签，点击上方按钮创建
            </div>
          )}
        </div>
      </Modal>

      {/* 图片预览 */}
      <ImageViewer
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
        imageUrl={previewImage}
      />

      {/* 手机记账二维码模态框 */}
      <Modal
        title="手机记账"
        open={qrcodeVisible}
        onCancel={() => setQrcodeVisible(false)}
        footer={null}
        width={400}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p style={{ marginBottom: '20px' }}>请使用手机扫描下方二维码，在手机上打开记账页面</p>
          <div style={{ display: 'inline-block', padding: '15px', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' }}>
            {qrCodeData && qrCodeData.qrCodeUrl ? (
              <img 
                src={qrCodeData.qrCodeUrl} 
                alt="手机记账二维码" 
                style={{ width: '200px', height: '200px' }}
                onError={(e) => {
                  console.error('二维码图片加载失败:', e);
                  e.target.src = `https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=QR%20code%20for%20mobile%20expense%20tracking%20app&image_size=square`;
                }}
              />
            ) : (
              <div style={{ width: '200px', height: '200px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f5f5f5' }}>
                <p>二维码生成中...</p>
              </div>
            )}
          </div>
          <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>提示：扫描二维码后，您可以在手机上方便地进行记账操作</p>
        </div>
      </Modal>
    </div>
  );
};

export default Expenses;